import _, {isNumber, isObject, isString} from 'lodash';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {useLocalize} from '../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage} from '../../../../libs/shared/core/src/helpers/internationalization/message';
import {useConstant} from '../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {Dictionary} from '../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {FrameTypesEnum} from '../../../../libs/shared/core/src/models/api/apiTypes';
import {inboxHasColor} from '../../../../libs/shared/core/src/models/helpers/inboxHelpers';
import {isTeamNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {buildTeammateDisplayName} from '../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {hasDebugFeature} from '../../../../libs/shared/core/src/services/debug/debug';
import {AppContentTypesEnum} from '../../../../libs/shared/core/src/store/panels/app/commonAppPanelState';
import {ColorTeammateAvatar} from '../../../../libs/shared/ui/src/components/avatar/colorTeammateAvatar';
import {DropdownCategory} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownCategory';
import {DropdownEmpty} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownEmpty';
import {DropdownItemAvatar} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemAvatar';
import {DropdownItemTeamAvatar} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemTeamAvatar';
import {DropdownVirtualList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownVirtualList';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemIcon} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemIcon';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {Layer} from '../../../../libs/shared/ui/src/components/layers/layer';
import {ColorDot} from '../../../../libs/shared/ui/src/components/utility/colorDot';
import {shadows} from '../../../../libs/shared/ui/src/styles/colorStyles';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {center} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {PreferencesModel} from '../../../models/api/preferencesModel';
import {SidePanelFrame} from '../../../store/stateless/sidePanelFrameSelectors';
import {DropdownItemLensIcon} from '../../common/dropdowns/dropdownItemLensIcon';
import {DropdownItemTagIcon} from '../../common/dropdowns/dropdownItemTagIcon';
import {DropdownItemTeammateAvatarWithStatus} from '../../common/dropdowns/dropdownItemTeammateAvatarWithStatus';
import {DropdownItemTeammateBadges} from '../../common/dropdowns/dropdownItemTeammateBadges';
import {computeTicketStatusCategoryFromInboxesFilter, Filter} from '../cells/header/cellsHeaderFilters';
import {TicketStatusIndicator} from '../ticketing/ticketStatusIndicator';
import {
  CommandPaletteCategoriesEnum,
  commandPaletteCategoriesRenderer,
  CommandPaletteFrameItem,
  CommandPaletteItem,
  CommandPaletteItemTypesEnum,
  CommandPaletteReduxActionsEnum,
  fuzzySearchPalette,
  isCommandPaletteCategory,
} from './commandPaletteCommonTypes';
import {CommandPaletteKeyboardFormatter} from './commandPaletteKeyboardFormatter';
import {CommandPaletteSearchInput} from './commandPaletteSearchInput';
import {CommandPaletteSeparator} from './commandPaletteSeparator';

const StyledBackgroundDiv = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${(p) => p.theme.modalBackdrop};
`;

const StyledContentDiv = styled.div`
  position: relative;
  height: 100%;
  pointer-events: none;
  ${center()};
`;

const StyledPaletteWrapperDiv = styled.div`
  height: 580px;
`;

const StyledPaletteDiv = styled.div`
  pointer-events: all;
  display: block;
  margin: 0 auto;
  width: 400px;
  border-radius: 16px;
  background-color: ${(p) => p.theme.foreground};
  ${shadows.dialogsAndDropdowns};
`;

const StyledPaletteHeader = styled.div`
  padding: 20px;
  padding-bottom: 7px;
`;

const StyledPaletteList = styled(DropdownVirtualList)`
  margin: 0;
  padding-bottom: 1px;

  > div {
    border-radius: 20px;
    padding-top: 0;
    padding-bottom: 7px;
  }
`;

const StyledPaletteItem = styled(DropdownItem)`
  border-radius: 8px;
  margin: 0 8px;
  position: relative;
`;

const StyledPaletteItemScore = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  font-size: 10px;
  color: ${(p) => p.theme.greys.black};
  z-index: 9;
`;

const StyledPaletteCategory = styled(DropdownCategory)`
  margin: 0 8px;
`;

const StyledShortcutsDiv = styled.div`
  grid-area: right-icon;
  align-self: center;
  margin-left: 8px;
`;

const StyledColorDotDiv = styled.div`
  grid-area: left-icon;
  margin-right: 8px;
  width: 20px;
  height: 20px;
  ${center()};
`;

const StyledIntegrationFrameCardAvatar = styled.div`
  grid-area: left-icon;
  margin-right: 8px;
  align-self: center;

  width: 20px;
  height: 20px;
  border-radius: 50%;

  overflow: hidden;
  border-radius: 15px 15px;
  background-color: ${(p) => p.theme.foreground};

  img {
    width: 20px;
    height: 20px;
  }
`;

const StyledBoxedIcon = styled.div<{$color: string}>`
  grid-area: left-icon;
  margin-right: 8px;
  align-self: center;

  width: 20px;
  height: 20px;
  border-radius: 50%;
  color: ${(p) => p.theme.greys.white};
  background-color: ${(p) => p.$color};

  ${center()};
`;

const StyledDropdownEmpty = styled(DropdownEmpty)`
  padding-top: 46px;
  padding-bottom: 50px;
`;

export interface CommandPaletteProps {
  onRequestClose: () => void;
  openLink: (arg: string) => void;
  openSideFrame: (arg: string) => void;
  onKeyboardAction: (arg: KeyboardActionsEnum) => void;
  onPreferenceAction: (preferencePatch: Partial<PreferencesModel>) => void;
  onReduxAction: (action: CommandPaletteReduxActionsEnum) => void;
  switchToFilter: (filter: Filter) => void;
  switchToApp: (arg: AppContentTypesEnum) => void;
  items: ReadonlyArray<CommandPaletteItem>;
  actionItems: ReadonlyArray<CommandPaletteItem>;
  appTabsItems: ReadonlyArray<CommandPaletteItem>;
  teamsNamesMap?: Dictionary<string>;
}

// Debug value
const showScores = hasDebugFeature('palette-scores');

export const CommandPalette: FC<CommandPaletteProps> = (props) => {
  const {onRequestClose} = props;
  const theme = useUserTheme();
  const localize = useLocalize();
  const [searchQuery, setSearchQuery] = useState('');
  const baseItems = useConstant(() => props.items);
  const boldItems = useMemo(() => baseItems.filter((i) => 'isBold' in i && i.isBold), [baseItems]);

  const items: ReadonlyArray<CommandPaletteItem> = useMemo(() => {
    if (!searchQuery) {
      if (boldItems.length > 0) {
        return boldItems.map((i) => ({
          ...i,
          parentCategoryId: CommandPaletteCategoriesEnum.UNREAD,
        }));
      }

      return baseItems;
    }

    const searchResults = fuzzySearchPalette(
      [...props.actionItems, ...baseItems, ...props.appTabsItems],
      searchQuery,
      showScores,
    );

    return searchResults;
  }, [boldItems, props.actionItems, props.appTabsItems, baseItems, searchQuery]);

  const groupedItems = useMemo(() => {
    const mapOfGroups = _(items)
      .filter((i) => Boolean(i.parentCategoryId))
      .compact()
      .groupBy((i) => i.parentCategoryId)
      .value();

    return _(mapOfGroups)
      .keys()
      .map((key) => {
        const catItems = mapOfGroups[key];
        if (!catItems) {
          return undefined;
        }

        return {
          items: catItems,
          category: key,
        };
      })
      .compact()
      .map((group, groupIndex, array) => {
        const isNotLast = groupIndex + 1 < array.length;

        if (isNotLast) {
          return [group.category, ...group.items, {key: `separator-${group.category}`}];
        }

        return [group.category, ...group.items];
      })
      .flatten()
      .value();
  }, [items]);

  // Compute an array of itemIds in order to have the "actual" index of items later on.
  const itemIds = useMemo(() => {
    const array: Array<string | number> = [];

    groupedItems.forEach((i) => {
      if (isObject(i) && 'itemId' in i) {
        array.push(i.itemId);
      }
    });
    return array;
  }, [groupedItems]);

  const renderRow = (index: number) => {
    const item = groupedItems[index];
    if (item === undefined) {
      return null;
    }

    if (isObject(item) && 'key' in item) {
      return <CommandPaletteSeparator key={item.key} />;
    }

    if (isString(item)) {
      if (isCommandPaletteCategory(item)) {
        const categoryRenderer = commandPaletteCategoriesRenderer[item];

        if (!categoryRenderer) {
          return null;
        }

        return <StyledPaletteCategory key={String(item)}>{categoryRenderer(localize)}</StyledPaletteCategory>;
      }
      if (isTeamNamespace(item) && props.teamsNamesMap) {
        return (
          <StyledPaletteCategory key={`team-cat-${item}`}>{props.teamsNamesMap[item]}</StyledPaletteCategory>
        );
      }

      return null;
    }

    const onSelect = () => {
      switch (item.type) {
        case CommandPaletteItemTypesEnum.ROUTE:
        case CommandPaletteItemTypesEnum.INBOX:
        case CommandPaletteItemTypesEnum.TEAM:
        case CommandPaletteItemTypesEnum.TEAMMATE:
        case CommandPaletteItemTypesEnum.LENS:
        case CommandPaletteItemTypesEnum.CUSTOM_VIEW: {
          props.openLink(item.path);
          break;
        }

        case CommandPaletteItemTypesEnum.APP: {
          props.switchToApp(item.contentType);
          break;
        }

        case CommandPaletteItemTypesEnum.FILTER: {
          props.switchToFilter(item.filter);
          break;
        }

        case CommandPaletteItemTypesEnum.FRAME: {
          props.openSideFrame(item.frame.frameId);
          break;
        }

        case CommandPaletteItemTypesEnum.KEYBOARD_ACTION: {
          if (item.keyboardAction) {
            props.onKeyboardAction(item.keyboardAction);
          }
          break;
        }

        case CommandPaletteItemTypesEnum.PREFERENCE_ACTION:
          props.onPreferenceAction(item.preferencePatch);
          break;

        case CommandPaletteItemTypesEnum.REDUX_ACTION:
          props.onReduxAction(item.reduxAction);
          break;

        default: {
          break;
        }
      }
    };

    const actualIndex = itemIds.includes(item.itemId) ? itemIds.indexOf(item.itemId) : undefined;

    return (
      <StyledPaletteItem index={actualIndex} key={String(item.itemId)} onSelect={onSelect}>
        {isNumber(item.score) && <StyledPaletteItemScore>{item.score}</StyledPaletteItemScore>}
        {renderItemContent(item, Boolean(searchQuery || boldItems.length > 0), theme)}
        {item.keyboardAction && (
          <StyledShortcutsDiv>
            <CommandPaletteKeyboardFormatter action={item.keyboardAction} />
          </StyledShortcutsDiv>
        )}
      </StyledPaletteItem>
    );
  };

  const getRowHeight = (index: number) => {
    const item = groupedItems[index];

    if (!item) {
      return undefined;
    }

    if (isObject(item) && 'key' in item) {
      return 15;
    }

    return 30;
  };

  const showResults = items.length > 0;

  const renderList = () => {
    if (showResults) {
      return (
        <StyledPaletteList
          onRequestClose={onRequestClose}
          shouldSelectFirst
          renderRow={renderRow}
          getRowHeight={getRowHeight}
          width={400}
          maxHeight={504}
          rowCount={groupedItems.length}
          selectableRowCount={itemIds.length}
          key={itemIds.toString()}
        />
      );
    }

    return <StyledDropdownEmpty />;
  };

  return (
    <Layer isInteractive testId="command-palette">
      <StyledBackgroundDiv onClick={onRequestClose} />
      <StyledContentDiv>
        <StyledPaletteWrapperDiv>
          <StyledPaletteDiv>
            <StyledPaletteHeader>
              <CommandPaletteSearchInput
                placeholder={localize(
                  defineMessage({
                    id: 'palette-search-placeholder',
                    message: 'Go to: inboxes, tags, and more',
                  }),
                )}
                value={searchQuery}
                onChange={setSearchQuery}
              />
            </StyledPaletteHeader>
            {renderList()}
          </StyledPaletteDiv>
        </StyledPaletteWrapperDiv>
      </StyledContentDiv>
    </Layer>
  );
};

function renderItemContent(item: CommandPaletteItem, noNestingLevel: boolean, theme: UserTheme) {
  switch (item.type) {
    case CommandPaletteItemTypesEnum.INBOX: {
      const color = inboxHasColor(item.inbox) ? item.inbox.color : theme.greys.shade50;

      return (
        <>
          <StyledColorDotDiv>
            <ColorDot color={color} size={8} />
          </StyledColorDotDiv>
          <DropdownItemContent isBold={item.isBold}>{item.label}</DropdownItemContent>
        </>
      );
    }

    case CommandPaletteItemTypesEnum.ROUTE:
    case CommandPaletteItemTypesEnum.APP:
    case CommandPaletteItemTypesEnum.KEYBOARD_ACTION:
    case CommandPaletteItemTypesEnum.PREFERENCE_ACTION:
    case CommandPaletteItemTypesEnum.REDUX_ACTION: {
      return (
        <>
          <DropdownItemIcon label={item.label} iconName={item.icon} />
          <DropdownItemContent isBold={item.isBold}>{item.label}</DropdownItemContent>
        </>
      );
    }

    case CommandPaletteItemTypesEnum.CUSTOM_VIEW: {
      return (
        <>
          <DropdownItemTagIcon
            nestingLevel={noNestingLevel ? 0 : item.customView.nestingLevel}
            tag={item.customView.tag}
          />
          <DropdownItemContent isBold={item.isBold}>{item.label}</DropdownItemContent>
        </>
      );
    }

    case CommandPaletteItemTypesEnum.LENS: {
      return (
        <>
          <DropdownItemLensIcon lens={item.lens} />
          <DropdownItemContent isBold={item.isBold}>{item.label}</DropdownItemContent>
        </>
      );
    }

    case CommandPaletteItemTypesEnum.TEAM: {
      return (
        <>
          <DropdownItemTeamAvatar team={item.team} />
          <DropdownItemContent isBold={item.isBold}>{item.label}</DropdownItemContent>
        </>
      );
    }

    case CommandPaletteItemTypesEnum.TEAMMATE: {
      return (
        <>
          <DropdownItemTeammateAvatarWithStatus teammate={item.teammate} onlineStatus={item.onlineStatus} />
          <DropdownItemContent>{buildTeammateDisplayName(item.teammate)}</DropdownItemContent>
          <DropdownItemTeammateBadges
            renderAsSecondaryContent
            availabilityStatus={item.teammate.availabilityStatus}
          />
        </>
      );
    }

    case CommandPaletteItemTypesEnum.FILTER: {
      const ticketStatusCategory = computeTicketStatusCategoryFromInboxesFilter(item.filter);

      return (
        <>
          {ticketStatusCategory ? (
            <DropdownItemIcon label={item.label}>
              <TicketStatusIndicator category={ticketStatusCategory} size={20} />
            </DropdownItemIcon>
          ) : (
            <DropdownItemIcon label={item.label} iconName={item.icon} />
          )}
          <DropdownItemContent isBold={item.isBold}>{item.label}</DropdownItemContent>
        </>
      );
    }

    case CommandPaletteItemTypesEnum.FRAME: {
      return renderPaletteFrameItem(item);
    }

    default:
      return null;
  }
}

function renderPaletteFrameItem(item: CommandPaletteFrameItem) {
  return (
    <>
      {renderPaletteFrameItemAvatar(item.frame)}
      <DropdownItemContent>{item.label}</DropdownItemContent>
    </>
  );
}

function renderPaletteFrameItemAvatar(frame: SidePanelFrame) {
  switch (frame.frameType) {
    case FrameTypesEnum.INTEGRATION: {
      return (
        <StyledIntegrationFrameCardAvatar>
          <img alt="integration-card" src={`${frame.iconsUrl}card.svg`} />
        </StyledIntegrationFrameCardAvatar>
      );
    }

    case FrameTypesEnum.ENTRY_POINT: {
      return (
        <DropdownItemAvatar>
          <ColorTeammateAvatar
            initials={frame.name[0] || 'P'}
            color={frame.color}
            size={VisualSizesEnum.MEDIUM}
          />
        </DropdownItemAvatar>
      );
    }

    case FrameTypesEnum.KEYBOARD_HELP: {
      return (
        <UserThemeConsumer>
          {(theme) => (
            <StyledBoxedIcon $color={theme.palette.grey.shade40}>
              <Icon name="keyboard" size={12} />
            </StyledBoxedIcon>
          )}
        </UserThemeConsumer>
      );
    }

    case FrameTypesEnum.CUSTOMER_VIEW: {
      return (
        <StyledBoxedIcon $color={frame.color}>
          <Icon name="contacts" size={12} />
        </StyledBoxedIcon>
      );
    }

    case FrameTypesEnum.CALENDAR: {
      return (
        <UserThemeConsumer>
          {(theme) => (
            <StyledBoxedIcon $color={theme.palette.red.shade40}>
              <Icon name="calendar" size={12} />
            </StyledBoxedIcon>
          )}
        </UserThemeConsumer>
      );
    }

    default: {
      return null;
    }
  }
}
